import { Api } from "../Api";
import { DataFrame, IDataFrame, Method, Status } from "../DataFrame";
import { ITypedEvent, TypedEvent } from "../TypedEvent";
import { ControllerConfigurationService } from "./Controller/ControllerConfigurationService";
import { IControllerConfigurationService } from "./Controller/IControllerConfigurationService";
import { AuthorizationInfo, BrokerEnvironment, BrokerInfo, ConnectionResult, ConnectionState, IExalusConnectionService, StreamHandler } from "./IExalusConnectionService";
import { ILoggerService } from "./Logging/ILoggerService";
import { LoggerService } from "./Logging/LoggerService";
import { AppState, IAppStateService } from "./IAppStateService";
import { AppStateService } from "./AppStateService";
import { SessionService } from "./Session/SessionService";
import { IUser } from "./Users/IUser";
import { IWebApiCacheService } from "./WebApi/IWebApiCacheService";
import { WebApiCacheService } from "./WebApi/WebApiCacheService";

export class LocalNetworkExalusConnectionService implements IExalusConnectionService {

	SendAndHandleStreamAsync<T>(dataFrame: IDataFrame<any>, streamHandler: StreamHandler<T>, logTransmission: boolean): Promise<void> {
		return Promise.reject(new Error("Streams are not supported over local network connection."));
	}

	private _errorOccuredEvent: ITypedEvent<[sender: string, data: string]> = new TypedEvent<[sender: string, data: string]>();
	private _connectionStateChangedEvent: ITypedEvent<ConnectionState> = new TypedEvent<ConnectionState>();
	private _dataReceivedEvent: ITypedEvent<DataFrame<any>> = new TypedEvent<IDataFrame<any>>();
	private _onMessageReceived: ITypedEvent<string> = new TypedEvent<string>();
	private _log: ILoggerService = Api.Get<ILoggerService>(LoggerService.ServiceName);
	private _controllerConfiguration: IControllerConfigurationService | null = null;
	private _cache: IWebApiCacheService | null = null;
	private _session: SessionService | null = null;
	public static readonly ServiceName: string = "ExalusConnectionService";
	private _logPackets: boolean = true;
	private socket: WebSocket | null = null;
	private _address: string | null = null;
	private _port: string = "81";
	private _isOpen: boolean = false;
	private _pin: string | undefined = undefined;
	private _serial: string | undefined = undefined;
	private _auth: AuthorizationInfo | null = null;
	private _initialized: boolean = false;
	private _pingInterval: number = 5000;
	private _pingIntervalId: any | null = null;
	private _lastReceivedPacketTime = Date.now();
	private _disconnectedOnPurpose = false;
	private _restoreTask: Promise<boolean> | null = null;

	constructor() {
		Api.Get<IAppStateService>(AppStateService.ServiceName).OnAppStateChanged().Subscribe(async (newState: AppState) => {
			switch (newState) {
				case AppState.ExitedLowPowerMode:
				case AppState.ReturnedFromSuspension:
					if (await this.RestoreConnectionAsync())
						this._session?.RestoreSessionAsync();
					break;
			}
		});
	}
	async GetConnectedBrokerInfoAsync(timeoutMs: number | null = 8_000): Promise<BrokerInfo> {
		const controller = await this.GetServerAddressAsync();
		return new BrokerInfo(this._address ?? "", controller ?? "", BrokerEnvironment.Local);
	}

	private Initialize(): void {
		if (this._initialized)
			return;

		this._initialized = true;
		this._controllerConfiguration = Api.Get<IControllerConfigurationService>(ControllerConfigurationService.ServiceName);
		this._cache = Api.Get<IWebApiCacheService>(WebApiCacheService.ServiceName);
		this._session = Api.Get<SessionService>(SessionService.ServiceName);
	}

	private async checkIfAuthInfoIsCorrectAsync(authInfo: AuthorizationInfo): Promise<boolean> {
		const url = `http://${window.location.hostname}/controller_info`;
		let res = await fetch(url, { method: 'GET' });

		if (res.ok)
			return await res.text() === `${authInfo.SerialNumber}:${authInfo.PIN}`;
		return false;
	}


	async PingControllerAsync(): Promise<boolean> {
		if (this.socket?.readyState !== WebSocket.OPEN)
			return false;

		if (this._lastReceivedPacketTime !== null && Date.now() - this._lastReceivedPacketTime < this._pingInterval)
			return false;

		let frame = new DataFrame<any>();
		frame.Resource = "/system/ping";
		frame.Method = Method.Get;

		let result = await this.SendAndWaitForResponseAsync(frame, 2000, false, false)
			.then(() => true)
			.catch(() => false);

		return result;
	}

	SubscribeTo<T>(resourceId: string, handler: { (data: IDataFrame<T>): void; }): () => void {
		let sub = (frame: any) => {
			if (frame.Resource === resourceId)
				handler(frame as IDataFrame<T>);
		};
		this.OnDataReceivedEvent().Subscribe(sub);
		return () => this.OnDataReceivedEvent().Unsubscribe(sub);
	}

	public GetControllerSerialNumber(): string | undefined {
		return this._serial;
	}

	public GetControllerPin(): string | undefined {
		return this._pin;
	}

	SetServersBrokerAddress(address: string): void {
		// Not used for local-network transport.
	}

	SetDefaultPacketsBrokerAddress(address: string): void {
		// Not used for local-network transport.
	}

	GetServerAddressAsync(): Promise<string | null> {
		if (Api.IsRunningFromLocalNetwork())
			this._address = `${window.location.hostname}:${this._port}`;

		return Promise.resolve(this._address);
	}

	ConnectAsync(address: string): Promise<ConnectionResult> {
		return new Promise((resolve, reject) => {
			this.Initialize();
			this._disconnectedOnPurpose = false;
			this._address = address;
			const previousSocket = this.socket;
			const socket = new WebSocket(`ws://${address}/api`);
			this.socket = socket;

			if (this._pingIntervalId !== null) {
				clearInterval(this._pingIntervalId);
				this._pingIntervalId = null;
			}

			previousSocket?.close();
			this._log.Debug(LocalNetworkExalusConnectionService.ServiceName, `Connecting to the WebSockets server ${address}`);

			socket.onerror = (ev) => {
				if (this.socket !== socket)
					return;

				this._log.Debug(LocalNetworkExalusConnectionService.ServiceName, `Error occured in the WebSockets server ${address} info: \n${JSON.stringify(ev)}`);
				this._isOpen = false;
				(this._errorOccuredEvent as TypedEvent<[string, string]>).Invoke(["WebSockets", ev.toString()]);
				return reject(ConnectionResult.FailedToConnect);
			};

			socket.onopen = (ev) => {
				if (this.socket !== socket)
					return;

				this._log.Debug(LocalNetworkExalusConnectionService.ServiceName, `Connected to the WebSockets server ${address} info: \n${JSON.stringify(ev)}`);
				this._isOpen = true;
				(this._connectionStateChangedEvent as TypedEvent<ConnectionState>).Invoke(ConnectionState.Connected);

				this._pingIntervalId = setInterval(async () => {
					await this.PingControllerAsync();
				}, this._pingInterval);

				return resolve(ConnectionResult.Connected);
			};

			socket.onclose = (ev) => {
				if (this.socket !== socket)
					return;

				this._log.Debug(LocalNetworkExalusConnectionService.ServiceName, `Disconnected from the WebSockets server ${address} info: \n${JSON.stringify(ev)}`);
				this._isOpen = false;
				if (this._pingIntervalId !== null) {
					clearInterval(this._pingIntervalId);
					this._pingIntervalId = null;
				}
				(this._connectionStateChangedEvent as TypedEvent<ConnectionState>).Invoke(ConnectionState.Disconnected);
			};

			socket.onmessage = (ev) => {
				if (this.socket !== socket)
					return;

				this._log.Debug(LocalNetworkExalusConnectionService.ServiceName, `Message received from the WebSockets server ${address}: ${ev.data}`);
				(this._onMessageReceived as TypedEvent<string>).Invoke(ev.data);
				try {
					let frame = JSON.parse(ev.data) as DataFrame<any>;
					this._log.Debug(LocalNetworkExalusConnectionService.ServiceName, `Received data frame: ${frame.Status}`);
					(this._dataReceivedEvent as TypedEvent<DataFrame<any>>).Invoke(frame);
				} catch (err) {
					this._log.Error(LocalNetworkExalusConnectionService.ServiceName, `Failed to parse message: ${ev.data}`);
				}
			};
			(this._connectionStateChangedEvent as TypedEvent<ConnectionState>).Invoke(ConnectionState.Connecting);
		});
	}

	async AuthorizeAsync(authorizationInfo: AuthorizationInfo): Promise<boolean> {
		Api.WorksInContextOf = authorizationInfo.SerialNumber;
		let res = await this.checkIfAuthInfoIsCorrectAsync(authorizationInfo);

		if (res) {
			this._log.Debug(LocalNetworkExalusConnectionService.ServiceName, `Authorization successful for controller ${authorizationInfo.SerialNumber}`);
			this._pin = authorizationInfo.PIN;
			this._serial = authorizationInfo.SerialNumber;
			this._auth = authorizationInfo;
			if (this.IsConnected())
				(this._connectionStateChangedEvent as TypedEvent<ConnectionState>).Invoke(ConnectionState.ConnectedAndAuthorized);
			return true;
		}
		else
			return false;
	}

	async ConnectAndAuthorizeAsync(authorizationInfo: AuthorizationInfo): Promise<ConnectionResult> {
		Api.WorksInContextOf = authorizationInfo.SerialNumber;
		this._log.Debug(LocalNetworkExalusConnectionService.ServiceName, `Connecting and authorizing to the controller ${authorizationInfo.SerialNumber}`);
		let addr = await this.GetServerAddressAsync();

		if (addr == null)
			return ConnectionResult.FailedToConnectToServer;

		const result = await this.ConnectAsync(addr);

		if (result !== ConnectionResult.Connected)
			return result;

		let autRes = await this.AuthorizeAsync(authorizationInfo);

		if (!autRes) {
			this.socket?.close();
			return ConnectionResult.AuthorizationFailed;
		}

		return ConnectionResult.Connected;
	}

	DisconnectAsync(): Promise<void> {
		this._log.Debug(LocalNetworkExalusConnectionService.ServiceName, `Disconnecting from the WebSockets server ${this._address}`);
		this._disconnectedOnPurpose = true;
		(this._connectionStateChangedEvent as TypedEvent<ConnectionState>).Invoke(ConnectionState.Disconnecting);

		if (this._pingIntervalId !== null)
			clearInterval(this._pingIntervalId);

		this.socket?.close();
		return Promise.resolve();
	}

	IsConnected(): boolean {
		return this._isOpen;
	}

	EnablePacketsLogging(): void {
		this._log.Debug(LocalNetworkExalusConnectionService.ServiceName, `Packets logging enabled`);
		this._logPackets = true;
	}

	DisablePacketsLogging(): void {
		this._log.Debug(LocalNetworkExalusConnectionService.ServiceName, `Packets logging disabled`);
		this._logPackets = false;
	}

	private SendData(data: DataFrame<any>, logTransmission: boolean) {
		let fr = JSON.stringify(data);

		if (logTransmission)
			this._log.Debug(LocalNetworkExalusConnectionService.ServiceName, `Sending data frame: ${fr}`);

		if (this._isOpen)
			this.socket?.send(fr);
		else
			this._log.Error(LocalNetworkExalusConnectionService.ServiceName, `Failed to send data frame: ${fr}`);

		return this._isOpen;
	}

	SendAsync(dataFrame: IDataFrame<any>): Promise<boolean> {
		if (this._isOpen)
			return Promise.resolve(this.SendData(dataFrame as DataFrame<any>, this._logPackets));
		else
			return Promise.resolve(false);
	}

	SendAndWaitForResponseAsync<T>(dataFrame: IDataFrame<any>, timeout: number, useCache: boolean, logTransmission: boolean = true): Promise<IDataFrame<T>> {
		return this.SendAndWaitForResponseWithRepeatAsync(dataFrame, timeout, useCache, true, logTransmission);
	}

	async SendAndHandleResponseAsync<T>(dataFrame: IDataFrame<any>, timeout: number, dataHandler: (result: IDataFrame<T>) => void, logTransmission: boolean): Promise<void> {
		let timeoutId = 0;
		const startTime = Date.now();

		if (!this.IsConnected()) {
			if (!await this.RestoreConnectionAsync())
				throw new Error("Connection is not established");
		}

		if (dataFrame.Resource !== "/users/user/login")
			await this._session?.WaitForSessionCreationAsync();

		return new Promise(async (resolve, reject) => {
			const enableTimeout = () => window.setTimeout(() => {
				this._dataReceivedEvent.Unsubscribe(onReceivedFrame);
				let errorMessage = `Response timeout, resource: ${dataFrame.Resource} method: ${dataFrame.Method} transaction id: ${dataFrame.TransactionId}`;
				this._log.Error(LocalNetworkExalusConnectionService.ServiceName, errorMessage);
				reject(new Error(errorMessage));
			}, timeout);

			let onReceivedFrame = async (receivedFrame?: IDataFrame<any>) => {
				if (receivedFrame?.TransactionId == dataFrame.TransactionId) {
					window.clearTimeout(timeoutId);
					const difference = Date.now() - startTime;

					if (logTransmission || (window as any)["packets"] === true)
						this._log.Debug(LocalNetworkExalusConnectionService.ServiceName, `Received response for: ${receivedFrame?.Resource} ${receivedFrame?.Method} id: ${receivedFrame?.TransactionId} in ${difference}ms`);

					if (receivedFrame?.Status === Status.UserIsNotLoggedIn) {
						this._dataReceivedEvent.Unsubscribe(onReceivedFrame);
						(this._session?.OnUserLoggedOutEvent() as TypedEvent<IUser>).Invoke(this._session?.User as IUser);
						try {
							await this._session?.RestoreSessionAsync();
							resolve(await this.SendAndHandleResponseAsync(dataFrame, timeout, dataHandler, logTransmission));
						} catch (error) {
							reject(error);
						}
						return;
					}

					switch (receivedFrame?.Status) {
						case Status.MultiDataResponseStart:
						case Status.MultiDataResponse:
							dataHandler(receivedFrame);
							timeoutId = enableTimeout();
							break;
						case Status.MultiDataResponseStop:
						case Status.FatalError:
						case Status.Error:
							dataHandler(receivedFrame);
							this._dataReceivedEvent.Unsubscribe(onReceivedFrame);
							resolve();
							break;
						default:
							let errorMessage = `Failed to process MultiDataResponse - recived incorrect response status: ${receivedFrame?.Status} method: ${dataFrame.Method} transaction id: ${dataFrame.TransactionId}
                             make sure that the requested endpoint uses MultiDataResponse, otherwise use SendAndWaitForResponseAsync<T> method.`;
							reject(new Error(errorMessage));
							break;
					}
				}
			};

			this._dataReceivedEvent.Subscribe(onReceivedFrame);

			timeoutId = enableTimeout();

			if (!this.SendData(dataFrame, logTransmission)) {
				let errorMessage = `Failed to send request, resource: ${dataFrame.Resource} method: ${dataFrame.Method} transaction id: ${dataFrame.TransactionId}`;
				reject(new Error(errorMessage));
			}
		});
	}

	async SendAndWaitForResponseWithRepeatAsync<T>(dataFrame: IDataFrame<any>, timeout: number, useCache: boolean, repeat = true, logTransmission: boolean = true): Promise<IDataFrame<T>> {
		if (dataFrame.Method === Method.Get && useCache) {
			if (!await this._controllerConfiguration?.DidCofigurationChangeAsync()) {
				let res = this._cache?.GetCache(dataFrame) as IDataFrame<T>;

				if (res != null) {
					return Promise.resolve(res);
				}
			}
		}

		let timeoutId = 0;
		const startTime = Date.now();

		if (!this.IsConnected()) {
			if (!await this.RestoreConnectionAsync())
				throw new Error("Connection is not established");
		}

		if (dataFrame.Resource !== "/users/user/login")
			await this._session?.WaitForSessionCreationAsync();

		return new Promise<IDataFrame<T>>(async (resolve, reject) => {
			let onReceivedFrame = async (receivedFrame?: IDataFrame<any>) => {
				if (receivedFrame?.TransactionId == dataFrame.TransactionId) {
					window.clearTimeout(timeoutId);
					this._dataReceivedEvent.Unsubscribe(onReceivedFrame);
					const difference = Date.now() - startTime;

					if (logTransmission)
						this._log.Debug(LocalNetworkExalusConnectionService.ServiceName, `Received response for: ${receivedFrame?.Resource} ${receivedFrame?.Method} id: ${receivedFrame?.TransactionId} in ${difference}ms`);

					if (dataFrame.Method === Method.Get && useCache && receivedFrame?.Status !== Status.UserIsNotLoggedIn)
						this._cache?.Cache(receivedFrame as DataFrame<any>);

                    if (receivedFrame?.Status === Status.UserIsNotLoggedIn && repeat) {
                        (this._session?.OnUserLoggedOutEvent() as TypedEvent<IUser>).Invoke(this._session?.User as IUser);
                        try {
                            await this._session?.RestoreSessionAsync();
                            resolve(await this.SendAndWaitForResponseWithRepeatAsync(dataFrame, timeout, useCache, false, logTransmission));
                        } catch (error) {
							reject(error);
						}
						return;
					}

					resolve(receivedFrame as IDataFrame<T>);
				}
			};

			this._dataReceivedEvent.Subscribe(onReceivedFrame);

			timeoutId = window.setTimeout(() => {
				this._dataReceivedEvent.Unsubscribe(onReceivedFrame);
				let errorMessage = `Response timeout, resource: ${dataFrame.Resource} method: ${dataFrame.Method} transaction id: ${dataFrame.TransactionId}`;
				this._log.Error(LocalNetworkExalusConnectionService.ServiceName, errorMessage);
				reject(new Error(errorMessage));
			}, timeout);

			if (!this.SendData(dataFrame, logTransmission)) {
				let errorMessage = `Failed to send request, resource: ${dataFrame.Resource} method: ${dataFrame.Method} transaction id: ${dataFrame.TransactionId}`;
				reject(new Error(errorMessage));
			}
		});
	}

	OnDataReceivedEvent(): ITypedEvent<IDataFrame<any>> {
		return this._dataReceivedEvent;
	}

	OnErrorOccuredEvent(): ITypedEvent<[sender: string, data: string]> {
		return this._errorOccuredEvent;
	}

	OnConnectionStateChangedEvent(): ITypedEvent<ConnectionState> {
		return this._connectionStateChangedEvent;
	}

	GetAuthorizationInfo(): AuthorizationInfo | null {
		return this._auth;
	}

	GetServiceName(): string {
		return LocalNetworkExalusConnectionService.ServiceName;
	}

	private async RestoreConnectionAsync(): Promise<boolean> {
		if (this._disconnectedOnPurpose)
			return false;

		if (this._restoreTask)
			return this._restoreTask;

		this._restoreTask = (async () => {
			const auth = this._auth;

			if (auth == null)
				return false;

			if (this.IsConnected())
				return this.AuthorizeAsync(auth);

			const addr = await this.GetServerAddressAsync();

			if (addr == null)
				return false;

			const connectionResult = await this.ConnectAsync(addr);

			if (connectionResult !== ConnectionResult.Connected)
				return false;

			return this.AuthorizeAsync(auth);
		})().finally(() => {
			this._restoreTask = null;
		});

		return this._restoreTask;
	}
}
